import { derived, get, writable, type Readable, type Writable } from 'svelte/store';

import * as selectraApi from 'src/services/selectra';
import type {
	SelectraDetailsResponse,
	SelectraQualificationResponse,
	SelectraPricesResponse,
} from 'src/types/Selectra';
import {
	clearLiveTariffFromUserSettings,
	syncLiveTariffFromSelectra,
} from 'src/helpers/selectra_tariff_helpers';

export const selectraQualification: Writable<Record<string, unknown>> = writable(null);
export const selectraPrices: Writable<SelectraPricesResponse> = writable(null);
export const selectraDetails: Writable<SelectraDetailsResponse | null> = writable(null);
export const selectraLiveTariffEnabled: Writable<boolean> = writable(false);
export const selectraLoading: Writable<boolean> = writable(false);
export const selectraError: Writable<string> = writable(null);

export const selectraIsQualified: Readable<boolean> = derived(
	selectraQualification,
	($selectraQualification) => Boolean($selectraQualification),
);

const isNotQualifiedError = (error: unknown): boolean => {
	const status =
		(error as { status?: number })?.status ??
		(error as { response?: { status?: number } })?.response?.status;
	const errorCode = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;

	return status === 400 && errorCode === 'SELECTRA_NOT_QUALIFIED';
};

/**
 * Loads the stored qualification, prices and live tariff status in one go.
 * Call this on mount of the Power Prices settings page (new layout only).
 */
export const loadSelectraState = async (): Promise<void> => {
	selectraLoading.set(true);
	selectraError.set(null);

	try {
		const inputs = await selectraApi.getStoredQualification();
		selectraQualification.set(inputs);
		await Promise.all([
			refreshSelectraPrices(),
			refreshSelectraLiveTariffStatus(),
			refreshSelectraDetails(),
		]);
	} catch (error) {
		if (isNotQualifiedError(error)) {
			selectraQualification.set(null);
			selectraPrices.set(null);
			selectraDetails.set(null);
			selectraLiveTariffEnabled.set(false);
		} else {
			console.error('Failed to load Selectra state', error);
			selectraError.set('failed_to_load');
		}
	} finally {
		selectraLoading.set(false);
	}
};

/**
 * Starts the qualification wizard by posting an empty inputs object.
 */
export const startSelectraQualification = async (): Promise<SelectraQualificationResponse> => {
	return selectraApi.postQualification({});
};

/**
 * Submits the accumulated inputs and returns either the next question(s)
 * or `done: true` when qualification is complete.
 */
export const submitSelectraQualificationStep = async (
	inputs: Record<string, unknown>,
): Promise<SelectraQualificationResponse> => {
	return selectraApi.postQualification(inputs);
};

/**
 * Called once the wizard reports `done: true` -- stores qualification inputs
 * and refreshes prices/live tariff so the plan card reflects the new state.
 */
export const finishSelectraQualification = async (inputs?: Record<string, unknown>): Promise<void> => {
	if (inputs) {
		selectraQualification.set(inputs);
	}

	await Promise.all([
		refreshSelectraPrices(),
		refreshSelectraLiveTariffStatus(),
		refreshSelectraDetails(),
	]);
};

export const removeSelectraQualification = async (): Promise<void> => {
	selectraLoading.set(true);

	try {
		if (get(selectraLiveTariffEnabled)) {
			await selectraApi.disableLiveTariff();
			selectraLiveTariffEnabled.set(false);
			clearLiveTariffFromUserSettings();
		}

		await selectraApi.deleteQualification();
		selectraQualification.set(null);
		selectraPrices.set(null);
		selectraDetails.set(null);
		selectraLiveTariffEnabled.set(false);
	} catch (error) {
		console.error('Failed to remove Selectra qualification', error);
		selectraError.set('failed_to_remove');
	} finally {
		selectraLoading.set(false);
	}
};

export const refreshSelectraPrices = async (): Promise<void> => {
	try {
		const prices = await selectraApi.getPrices();
		selectraPrices.set(prices);

		if (get(selectraLiveTariffEnabled)) {
			syncLiveTariffFromSelectra(prices);
		}
	} catch (error) {
		console.error('Failed to load Selectra prices', error);
		selectraError.set('failed_to_load_prices');
	}
};

export const refreshSelectraDetails = async (): Promise<void> => {
	try {
		const details = await selectraApi.getDetails();
		selectraDetails.set(details);
	} catch (error) {
		console.error('Failed to load Selectra details', error);
		selectraDetails.set(null);
	}
};

export const refreshSelectraLiveTariffStatus = async (): Promise<void> => {
	try {
		const status = await selectraApi.getLiveTariffStatus();
		selectraLiveTariffEnabled.set(Boolean(status?.enabled));
	} catch (error) {
		console.error('Failed to load Selectra live tariff status', error);
	}
};

export const toggleSelectraLiveTariff = async (enabled: boolean): Promise<boolean> => {
	const previous = get(selectraLiveTariffEnabled);
	selectraLiveTariffEnabled.set(enabled);

	try {
		if (enabled) {
			await selectraApi.enableLiveTariff();
			await refreshSelectraPrices();
		} else {
			await selectraApi.disableLiveTariff();
			clearLiveTariffFromUserSettings();
		}
		return true;
	} catch (error) {
		console.error('Failed to toggle Selectra live tariff', error);
		selectraLiveTariffEnabled.set(previous);
		selectraError.set('failed_to_toggle_live_tariff');
		return false;
	}
};
